import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { Section, SectionHeading, Eyebrow } from "@/components/Section";
import { Reveal, HeroItem, HeroStagger } from "@/components/Reveal";
import { cn } from "@/lib/cn";

const heroStats = [
  {
    value: "27%",
    label: "higher average ticket when techs present options on-site",
  },
  {
    value: "3.4x",
    label: "faster job-to-payment vs. mailed or emailed invoices",
  },
  {
    value: "61%",
    label: "of estimates closed before the tech leaves the driveway",
  },
];

const wizardStages = [
  {
    step: "01",
    name: "Arrive",
    body: "Job opens with the customer, property, equipment history, and dispatch notes already loaded — no calling the office to ask what they're walking into.",
  },
  {
    step: "02",
    name: "Diagnose",
    body: "Guided checklists by job type capture findings, meter readings, and photos in the order a real diagnostic actually happens.",
  },
  {
    step: "03",
    name: "Quote",
    body: "Pricebook-driven options build themselves from the diagnosis — no separate app, no retyping line items from a paper pad.",
  },
  {
    step: "04",
    name: "Authorize",
    body: "Customer reviews options and signs on the device. Scope, price, and approval are locked to the job record before a wrench turns.",
  },
  {
    step: "05",
    name: "Work",
    body: "Parts used, time on site, and job notes log against the authorized scope, so change orders are visible instead of assumed.",
  },
  {
    step: "06",
    name: "Payment",
    body: "Card on file, tap-to-pay, or financing — collected the moment the work is done, not chased down after the invoice goes out.",
  },
  {
    step: "07",
    name: "Complete",
    body: "Closeout pushes notes, media, parts, and payment state to ServiTrex in real time. The office sees a finished job, not a promise.",
  },
];

const tiers = [
  {
    name: "Good",
    tagline: "Get it running",
    price: "$189",
    detail: "Repair the failed component, restore the system to working order.",
    highlight: false,
  },
  {
    name: "Better",
    tagline: "Most customers pick this",
    price: "$412",
    detail:
      "Repair plus the two adjacent parts already showing wear — one trip instead of two.",
    highlight: true,
  },
  {
    name: "Best",
    tagline: "Fix it and stop the callback",
    price: "$1,050",
    detail:
      "Full assembly replacement with a two-year parts-and-labor warranty attached.",
    highlight: false,
  },
];

const deepDives = [
  {
    eyebrow: "Payments",
    title: "Get paid before the van leaves the driveway",
    body: "Tap-to-pay, saved cards, and financing hand-off live inside the same screen as the invoice — no separate terminal, no walking the customer to a laptop.",
    points: [
      "Card-on-file and contactless in one flow",
      "Split payments and deposits without office intervention",
      "Receipt sent the moment the charge clears",
    ],
    mock: {
      label: "Payment",
      title: "$412.00 due",
      lines: [
        "Card on file · Visa •••• 4471",
        "Tap to charge",
        "Receipt → customer email + SMS",
      ],
    },
    reverse: false,
  },
  {
    eyebrow: "Photos & notes",
    title: "The photo roll becomes the job record",
    body: "Before-and-after photos, meter readings, and voice-to-text notes attach directly to the job timeline — tagged, timestamped, and visible in the office the second they're captured.",
    points: [
      "Before/after tagging with one tap",
      "Voice-to-text notes transcribed on-device",
      "Every asset lands on the customer's permanent property history",
    ],
    mock: {
      label: "Job media",
      title: "8 photos · 2 notes",
      lines: [
        "Before: corroded coil",
        "After: replaced + sealed",
        'Note: "Recommend surge protector next visit"',
      ],
    },
    reverse: true,
  },
  {
    eyebrow: "Memberships",
    title: "Sell the membership at the door, not in a follow-up call",
    body: "Techs pitch maintenance plans with real pricing and real coverage while standing in front of the equipment — enrollment and billing start immediately, with tech credit tracked automatically.",
    points: [
      "Plan pricing and coverage pulled live from ServiTrex",
      "Enrollment and first billing complete on-site",
      "Tech attribution flows to payroll without a spreadsheet",
    ],
    mock: {
      label: "Membership",
      title: "Comfort Plan · $19/mo",
      lines: [
        "2 tune-ups/year · priority scheduling",
        "Enroll now → billing starts today",
        "Credited to: this technician",
      ],
    },
    reverse: false,
  },
  {
    eyebrow: "Offline mode",
    title: "The job doesn't stop when the signal does",
    body: "Basements, rural routes, and dead zones don't pause work. TrexField queues every photo, note, signature, and payment locally and syncs the instant the connection returns — nothing waits, nothing gets lost.",
    points: [
      "Full job workflow available with zero bars",
      "Queued actions sync automatically on reconnect",
      "Conflict-safe writes — the office never gets a duplicate or a gap",
    ],
    mock: {
      label: "Connection",
      title: "Offline · 4 actions queued",
      lines: [
        "2 photos · 1 signature · 1 payment",
        "Will sync automatically",
        "Nothing blocks the technician",
      ],
    },
    reverse: true,
  },
  {
    eyebrow: "Dispatch & GPS",
    title: "The office always knows where the truck actually is",
    body: "Live location, route progress, and status changes stream to the ServiTrex dispatch board in real time — so ETAs to the customer are accurate and the next job can be assigned without a phone call.",
    points: [
      "Live GPS on the dispatch map, not a stale check-in",
      "Automatic status changes: en route, on site, complete",
      "Accurate customer ETAs without a CSR guessing",
    ],
    mock: {
      label: "Dispatch",
      title: "En route · 11 min out",
      lines: [
        "1842 Harbor Ave",
        "Status pushed to office board",
        "Customer notified automatically",
      ],
    },
    reverse: false,
  },
];

const syncPoints = [
  "Job status, notes, and media — visible in the office the instant they're captured",
  "Payments and invoices — reconciled without a nightly import",
  "Membership enrollments — active in billing before the tech leaves the property",
  "GPS and route status — live on the same dispatch board CSRs use for the next call",
];

const outcomeStats = [
  {
    value: "27%",
    label: "higher average ticket from presented good/better/best options",
  },
  {
    value: "3.4x",
    label: "faster time from job complete to payment collected",
  },
  {
    value: "44%",
    label: "of eligible customers offered a membership at the door",
  },
  {
    value: "0",
    label: "end-of-day paperwork reconstruction — closeout happens on-site",
  },
];

export function ProductFieldPage() {
  return (
    <>
      <Head
        title="TrexField — The field-technician app that syncs with the office in real time"
        description="TrexField guides technicians from arrival to payment: on-site estimates, tap-to-pay, photo and note capture, membership sales, offline mode, and live GPS — all synced to ServiTrex instantly."
      />

      <Section className="pt-14 sm:pt-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <HeroStagger>
            <HeroItem>
              <Eyebrow>Product · Field</Eyebrow>
            </HeroItem>
            <HeroItem>
              <h1 className="max-w-2xl text-balance text-4xl text-fg sm:text-5xl lg:text-[3.3rem] lg:leading-[1.03]">
                Everything a technician needs on-site. Nothing the office finds
                out about later.
              </h1>
            </HeroItem>
            <HeroItem>
              <p className="mt-5 max-w-xl text-pretty text-lg text-muted">
                TrexField puts the whole job in a technician's pocket —
                diagnosis, pricing, authorization, payment, and closeout — and
                mirrors every move back to ServiTrex in real time. Built for
                HVAC, plumbing, electrical, and roofing crews standing in
                someone's driveway, not at a desk.
              </p>
            </HeroItem>
            <HeroItem>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button to="/demo" size="lg">
                  See TrexField in a demo
                </Button>
                <Button to="/product/office" variant="secondary" size="lg">
                  Explore the office product
                </Button>
              </div>
            </HeroItem>
            <HeroItem>
              <dl className="mt-10 grid max-w-xl grid-cols-3 gap-4 border-t border-border pt-6">
                {heroStats.map((stat) => (
                  <div key={stat.label}>
                    <dt className="font-display text-2xl font-semibold tracking-tight text-teal-deep sm:text-3xl">
                      {stat.value}
                    </dt>
                    <dd className="mt-1 text-xs leading-snug text-muted">
                      {stat.label}
                    </dd>
                  </div>
                ))}
              </dl>
            </HeroItem>
          </HeroStagger>

          <Reveal delay={0.1}>
            <div className="relative mx-auto w-full max-w-md">
              <div
                aria-hidden
                className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-teal/15 blur-3xl"
              />
              <figure className="overflow-hidden rounded-[2rem] border border-border bg-surface-raised shadow-glow">
                <img
                  src="/img/mktg-field-tech.jpg"
                  alt="A field technician reviewing a job in the TrexField app on a mobile device at a customer's property"
                  width={1200}
                  height={896}
                  className="aspect-[4/3] w-full object-cover"
                />
                <figcaption className="flex items-center justify-between gap-3 border-t border-border px-5 py-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                      Live · Harbor Ave
                    </p>
                    <p className="mt-1 font-display text-lg font-semibold tracking-tight">
                      No-cool diagnostic
                    </p>
                  </div>
                  <span className="rounded-full bg-surface-muted px-3 py-1 text-xs font-semibold text-fg">
                    Synced
                  </span>
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <SectionHeading
          eyebrow="The guided job wizard"
          title="One flow from arrival to invoice-ready closeout"
          description="TrexField doesn't hand a technician a blank job and a hope. Every visit moves through the same seven stages, so nothing gets skipped and nothing gets improvised."
        />
        <div className="relative mt-12">
          <div
            aria-hidden
            className="absolute left-0 right-0 top-6 hidden h-px bg-border lg:block"
          />
          <ol className="grid gap-5 lg:grid-cols-7 lg:gap-4">
            {wizardStages.map((stage, index) => (
              <Reveal key={stage.name} delay={index * 0.05} className="h-full">
                <li className="flex h-full flex-col rounded-2xl border border-border bg-surface-raised p-5">
                  <span className="relative z-10 grid h-9 w-9 place-items-center rounded-full bg-ink font-display text-sm font-semibold text-fg-inverse">
                    {stage.step}
                  </span>
                  <h3 className="mt-4 font-display text-lg tracking-tight">
                    {stage.name}
                  </h3>
                  <p className="mt-2 flex-1 text-pretty text-sm leading-relaxed text-muted">
                    {stage.body}
                  </p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div>
            <Eyebrow>On-site estimates</Eyebrow>
            <h2 className="text-balance text-3xl text-fg sm:text-4xl">
              Good, better, best — presented like options, not read off a
              clipboard
            </h2>
            <p className="mt-4 max-w-md text-pretty text-muted">
              The moment a diagnosis is entered, TrexField builds three
              pricebook-accurate options automatically. Technicians present a
              real menu on the screen instead of quoting one number and hoping
              it lands.
            </p>
            <p className="mt-4 max-w-md text-pretty text-sm text-muted">
              Middle-tier framing does the selling: customers anchor high,
              compare against "just fix it," and land on the option built to be
              chosen.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {tiers.map((tier, index) => (
              <Reveal key={tier.name} delay={index * 0.06} className="h-full">
                <article
                  className={cn(
                    "flex h-full flex-col rounded-[1.75rem] border p-5",
                    tier.highlight
                      ? "border-teal/50 bg-ink text-fg-inverse shadow-glow"
                      : "border-border bg-surface-raised",
                  )}
                >
                  <p
                    className={cn(
                      "text-xs font-semibold uppercase tracking-[0.16em]",
                      tier.highlight ? "text-teal" : "text-teal-deep",
                    )}
                  >
                    {tier.name}
                  </p>
                  <p
                    className={cn(
                      "mt-2 text-xs",
                      tier.highlight ? "text-fg-inverse/70" : "text-muted",
                    )}
                  >
                    {tier.tagline}
                  </p>
                  <p className="mt-4 font-display text-3xl tracking-tight">
                    {tier.price}
                  </p>
                  <p
                    className={cn(
                      "mt-3 flex-1 text-pretty text-sm leading-relaxed",
                      tier.highlight ? "text-fg-inverse/80" : "text-muted",
                    )}
                  >
                    {tier.detail}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <div className="space-y-20">
          {deepDives.map((item, index) => (
            <Reveal key={item.title} delay={index * 0.03}>
              <div
                className={cn(
                  "grid items-center gap-10 lg:grid-cols-2 lg:gap-14",
                  item.reverse && "lg:[&>*:first-child]:order-2",
                )}
              >
                <div>
                  <Eyebrow>{item.eyebrow}</Eyebrow>
                  <h2 className="text-balance text-3xl text-fg sm:text-4xl">
                    {item.title}
                  </h2>
                  <p className="mt-4 max-w-xl text-pretty text-muted">
                    {item.body}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {item.points.map((point) => (
                      <li key={point} className="flex gap-3 text-sm text-fg">
                        <span
                          aria-hidden
                          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-deep"
                        />
                        <span className="text-pretty">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mx-auto w-full max-w-sm">
                  <div className="overflow-hidden rounded-[1.75rem] border border-border bg-surface-raised shadow-soft">
                    <div className="border-b border-border px-5 py-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                        {item.mock.label}
                      </p>
                      <p className="mt-2 font-display text-xl font-semibold tracking-tight">
                        {item.mock.title}
                      </p>
                    </div>
                    <div className="space-y-3 p-5">
                      {item.mock.lines.map((line) => (
                        <div
                          key={line}
                          className="rounded-xl border border-border bg-surface-muted/60 px-4 py-3 text-sm text-fg"
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
          <div>
            <Eyebrow>Real-time sync</Eyebrow>
            <h2 className="text-balance text-3xl text-fg sm:text-4xl">
              TrexField and ServiTrex share one job record — not a nightly
              export
            </h2>
            <p className="mt-4 max-w-xl text-pretty text-muted">
              There's no batch job reconciling two databases at midnight. What a
              technician does in the field is what the office sees, the moment
              it happens — and what a CSR does in ServiTrex is what shows up on
              the technician's screen.
            </p>
            <div className="mt-8">
              <Button to="/product/office" variant="secondary">
                See how the office product uses this data →
              </Button>
            </div>
          </div>

          <Reveal delay={0.05}>
            <ul className="space-y-3">
              {syncPoints.map((point) => (
                <li
                  key={point}
                  className="rounded-2xl border border-border bg-surface-raised px-5 py-4 text-sm text-fg shadow-soft"
                >
                  {point}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <SectionHeading
          eyebrow="Outcomes"
          title="What operators see once techs run the whole job on TrexField"
        />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {outcomeStats.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 0.05} className="h-full">
              <div className="h-full rounded-3xl border border-border bg-surface-raised p-6 shadow-soft">
                <p className="font-display text-4xl tracking-tight text-teal-deep">
                  {stat.value}
                </p>
                <p className="mt-3 text-pretty text-sm leading-relaxed text-muted">
                  {stat.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <CtaBand
        title="Put TrexField on a real route in your demo"
        body="Walk a live job from arrival to payment, then see the same record land in the ServiTrex office product in real time."
      />
    </>
  );
}
